import { KatalMonitoringDriver, KatalMonitoringDriverOptions } from '@amzn/katal-monitoring-aws-driver';
import { Publisher, Metric, Context } from '@amzn/katal-metrics';
import KatalMetricString from '@amzn/katal-metrics/lib/metricObject/KatalMetricString';
import { getMonitoringConfig, getStage } from './environmentUtils';
import { getBrowserInfo, getPlatform, getTimezone } from './clientEnvUtils';
import { logger } from './logger';

// Global metrics publisher instance
export let metricsPublisher: Publisher;
let metricsUserAlias: string | undefined;

/** Shared user-environment dimensions — set once during configureKatalMetrics */
let metricsUserEnv: Record<string, string> = {};

/** Returns the current user-environment dimensions for use in metric publishers */
export const getMetricsUserEnv = (): Record<string, string> => metricsUserEnv;

/**
 * Metric categories for better organization
 */
export enum MetricCategory {
  USER_ACTION = 'UserAction',
  PERFORMANCE = 'Performance',
  API_CALL = 'APICall',
  BUSINESS = 'Business',
  ERROR = 'Error',
  NAVIGATION = 'Navigation',
}

/**
 * Configure and initialize Katal Metrics
 * @param userAlias - User's alias for attribution
 * @param displayName - User's display name
 */
export const configureKatalMetrics = (userAlias: string, displayName: string): void => {
  if (metricsPublisher) {
    logger.warn('Metrics already initialized, skipping re-initialization', { userAlias });
    return;
  }

  metricsUserAlias = userAlias;
  const { name: browser, version: browserVersion } = getBrowserInfo();
  metricsUserEnv = {
    userAlias,
    displayName,
    timezone: getTimezone(),
    browser,
    browserVersion,
    platform: getPlatform(),
  };

  try {
    // Configure the AWS Driver
    const metricsConfig: KatalMonitoringDriverOptions = {
      url: getMonitoringConfig().metricsUrl,
    };

    const metricsDriver = new KatalMonitoringDriver(metricsConfig);

    // Create metrics context — site/serviceName form the EMF namespace: Katal/{site}/{serviceName}
    // Must match the namespace used in CloudWatch dashboards: Katal/Verity/{stage}
    const metricsContext = new Context({
      site: 'Verity',
      serviceName: getStage(),
    });

    // Create metrics publisher (driver, errorHandler, context)
    metricsPublisher = new Publisher(
      metricsDriver,
      (error: Error) => {
        // Error handler for failed metric publishing
        logger.error('Metrics publishing failed', {
          error: error?.message || 'Unknown error',
          stack: error?.stack,
        });
      },
      metricsContext
    );

    // Log successful initialization
    logger.info('Katal Metrics initialized successfully', {
      userAlias,
      displayName,
    });
  } catch (error) {
    // If metrics initialization fails, log but don't break the application
    logger.error('Failed to initialize Katal Metrics', {
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Convert a key-value record into KatalMetricString[] for use as cloudWatchDimensions.
 * Fields passed here appear as top-level fields in the EMF log AND as CloudWatch dimensions.
 * Use sparingly — high-cardinality dimensions increase CloudWatch costs.
 */
const toCloudWatchDimensions = (fields: Record<string, string>): KatalMetricString[] =>
  Object.entries(fields).map(([key, value]) => new KatalMetricString(key, value));

/**
 * Publish a counter metric.
 *
 * IMPORTANT: Only `methodName` and `cloudWatchDimensions` from the context reach the EMF log.
 * The KatalMonitoring driver ignores all other context fields. Any field that must appear in
 * CloudWatch Logs Insights queries MUST be passed via `emfFields` (becomes `dimensions` in EMF).
 *
 * @param metricName - Name of the metric
 * @param value - Counter value (default 1)
 * @param category - Maps to `methodName` dimension in EMF
 * @param emfFields - Fields emitted as CloudWatch dimensions (appear in Logs Insights queries).
 *                     Use sparingly — high-cardinality dimensions increase CloudWatch costs.
 */
export const publishCounter = (
  metricName: string,
  value: number = 1,
  category?: MetricCategory,
  emfFields?: Record<string, string>
): void => {
  try {
    if (!metricsPublisher) {
      logger.warn('Metrics publisher not initialized. Skipping metric publication.', { metricName });
      return;
    }

    const needsChildPublisher = category || emfFields;

    if (needsChildPublisher) {
      const context: Record<string, unknown> = {
        methodName: category || 'General',
        ...(emfFields && { cloudWatchDimensions: toCloudWatchDimensions(emfFields) }),
      };
      const publisher = metricsPublisher.newChildPublisher(context);
      publisher.publish(new Metric.Counter(metricName, value));
    } else {
      metricsPublisher.publish(new Metric.Counter(metricName, value));
    }
  } catch (error) {
    logger.error('Failed to publish counter metric', {
      metricName,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Publish a timer metric.
 *
 * See publishCounter for details on how context fields reach EMF logs.
 *
 * @param metricName - Name of the metric
 * @param durationMs - Duration in milliseconds
 * @param category - Maps to `methodName` dimension in EMF
 * @param emfFields - Fields emitted as CloudWatch dimensions
 */
export const publishTimer = (
  metricName: string,
  durationMs: number,
  category?: MetricCategory,
  emfFields?: Record<string, string>
): void => {
  try {
    if (!metricsPublisher) {
      logger.warn('Metrics publisher not initialized. Skipping metric publication.', { metricName });
      return;
    }

    const needsChildPublisher = category || emfFields;

    if (needsChildPublisher) {
      const context: Record<string, unknown> = {
        methodName: category || 'General',
        ...(emfFields && { cloudWatchDimensions: toCloudWatchDimensions(emfFields) }),
      };
      const publisher = metricsPublisher.newChildPublisher(context);
      publisher.publish(new Metric.Timer(metricName, durationMs));
    } else {
      metricsPublisher.publish(new Metric.Timer(metricName, durationMs));
    }
  } catch (error) {
    logger.error('Failed to publish timer metric', {
      metricName,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Publish a string metric.
 * Note: String metrics are NOT formatted as EMF — they are logged as-is with httpData.
 * All fields in emfFields still appear in the log entry (merged by Lambda).
 *
 * @param metricName - Name of the metric
 * @param value - String value
 * @param category - Maps to `methodName` in the log
 * @param emfFields - Additional fields included in the log entry
 */
export const publishString = (
  metricName: string,
  value: string,
  category?: MetricCategory,
  emfFields?: Record<string, string>
): void => {
  try {
    if (!metricsPublisher) {
      logger.warn('Metrics publisher not initialized. Skipping metric publication.', { metricName });
      return;
    }

    const needsChildPublisher = category || emfFields;

    if (needsChildPublisher) {
      const context: Record<string, unknown> = {
        methodName: category || 'General',
        ...(emfFields && { cloudWatchDimensions: toCloudWatchDimensions(emfFields) }),
      };
      const publisher = metricsPublisher.newChildPublisher(context);
      publisher.publish(new Metric.String(metricName, value));
    } else {
      metricsPublisher.publish(new Metric.String(metricName, value));
    }
  } catch (error) {
    logger.error('Failed to publish string metric', {
      metricName,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Publish a timed attempt metric (combines timer + success/failure).
 * See publishCounter for details on how context fields reach EMF logs.
 *
 * @param metricName - Name of the metric
 * @param durationMs - Duration in milliseconds
 * @param success - Whether the attempt was successful
 * @param category - Maps to `methodName` dimension in EMF
 * @param emfFields - Fields emitted as CloudWatch dimensions
 */
export const publishTimedAttempt = (
  metricName: string,
  durationMs: number,
  success: boolean,
  category?: MetricCategory,
  emfFields?: Record<string, string>
): void => {
  try {
    if (!metricsPublisher) {
      logger.warn('Metrics publisher not initialized. Skipping metric publication.', { metricName });
      return;
    }

    const timedAttempt = new Metric.TimedAttempt(metricName);
    if (success) {
      timedAttempt.setSuccess();
    } else {
      timedAttempt.setFailure();
    }
    timedAttempt.setLatency(durationMs);

    const needsChildPublisher = category || emfFields;

    if (needsChildPublisher) {
      const context: Record<string, unknown> = {
        methodName: category || 'General',
        ...(emfFields && { cloudWatchDimensions: toCloudWatchDimensions(emfFields) }),
      };
      const publisher = metricsPublisher.newChildPublisher(context);
      publisher.publish(timedAttempt);
    } else {
      metricsPublisher.publish(timedAttempt);
    }
  } catch (error) {
    logger.error('Failed to publish timed attempt metric', {
      metricName,
      error: error instanceof Error ? error.message : 'Unknown error',
    });
  }
};

/**
 * Track user action
 * @param action - Action name
 * @param details - Additional dimension fields
 */
export const trackUserAction = (action: string, details?: Record<string, string>): void => {
  publishCounter(action, 1, MetricCategory.USER_ACTION, details);
};

/**
 * Track page navigation
 * @param pageName - Full normalized page name (e.g. "manage-programs/product-code/rpn")
 */
export const trackPageView = (pageName: string): void => {
  const section = pageName.split('/')[0] || 'home';
  publishCounter('PageView', 1, MetricCategory.NAVIGATION, {
    pageName,
    section,
    ...(metricsUserAlias && { userAlias: metricsUserAlias }),
  });
};

/**
 * Track performance measurement
 * @param operation - Operation name
 * @param durationMs - Duration in milliseconds
 * @param details - Additional dimension fields
 */
export const trackPerformance = (operation: string, durationMs: number, details?: Record<string, string>): void => {
  publishTimer(operation, durationMs, MetricCategory.PERFORMANCE, details);
};

/**
 * Track API call metrics
 * @param endpoint - API endpoint
 * @param method - HTTP method
 * @param durationMs - Duration in milliseconds
 * @param success - Whether the call was successful
 * @param statusCode - HTTP status code (optional)
 */
export const trackApiCall = (
  endpoint: string,
  method: string,
  durationMs: number,
  success: boolean,
  statusCode?: number
): void => {
  publishTimedAttempt('APICall', durationMs, success, MetricCategory.API_CALL, {
    endpoint,
    method,
    ...(statusCode && { statusCode: statusCode.toString() }),
  });
};

/**
 * Track business metric
 * @param metricName - Business metric name
 * @param value - Metric value
 * @param details - Additional dimension fields
 */
export const trackBusinessMetric = (metricName: string, value: number, details?: Record<string, string>): void => {
  publishCounter(metricName, value, MetricCategory.BUSINESS, details);
};

/**
 * Track error occurrence
 * @param errorType - Type of error
 * @param details - Additional dimension fields
 */
export const trackError = (errorType: string, details?: Record<string, string>): void => {
  publishCounter('Error', 1, MetricCategory.ERROR, { errorType, ...details });
};

/**
 * Track feature usage
 * @param featureName - Name of the feature used
 * @param action - Action performed (optional)
 */
export const trackFeatureUsage = (featureName: string, action?: string): void => {
  publishCounter('FeatureUsage', 1, MetricCategory.USER_ACTION, {
    featureName,
    ...(action && { action }),
  });
};

/**
 * Check if metrics publisher is initialized
 * @returns boolean indicating if metrics are available
 */
export const isMetricsInitialized = (): boolean => {
  return !!metricsPublisher;
};

/**
 * Reset metrics state — for testing only.
 * @internal
 */
export const resetMetricsForTesting = (): void => {
  metricsPublisher = undefined as unknown as Publisher;
  metricsUserAlias = undefined;
  metricsUserEnv = {};
};

/**
 * Normalizes a route pathname into a stable metric name by stripping dynamic ID segments.
 *
 * Examples:
 *   /manage-programs/product-code/ABC123              → "manage-programs/product-code"
 *   /manage-programs/product-code/ABC123/rpn/R456     → "manage-programs/product-code/rpn"
 *   /manage-programs/product-code/ABC123/rpn/R456/fpn/F789 → "manage-programs/product-code/rpn/fpn"
 *   /reports/PRM_DETAILED                             → "reports/PRM_DETAILED"
 */
export const normalizePathname = (pathname: string): string => {
  const fpnMatch = pathname.match(/^\/manage-programs\/product-code\/[^/]+\/rpn\/[^/]+\/fpn\//);
  if (fpnMatch) return 'manage-programs/product-code/rpn/fpn';

  const rpnMatch = pathname.match(/^\/manage-programs\/product-code\/[^/]+\/rpn\//);
  if (rpnMatch) return 'manage-programs/product-code/rpn';

  const productCodeMatch = pathname.match(/^\/manage-programs\/product-code\//);
  if (productCodeMatch) return 'manage-programs/product-code';

  const reportMatch = pathname.match(/^\/reports\/([^/]+)/);
  if (reportMatch) return `reports/${reportMatch[1]}`;

  return pathname.replace(/^\//, '') || 'home';
};
